import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';

const AUTO_CLOSE_SECONDS = 5;

const CTA_CLASS =
    'flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-xl font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c]';

export interface BulkEnquirySuccessProps {
    /**
     * `existing` confirms and auto-closes; `new` welcomes the fresh account and
     * leads into onboarding — mirroring the single-enquiry gate so the two
     * journeys report the same funnel steps.
     */
    variant: 'existing' | 'new';
    /** How many professionals the selection was split across. */
    supplierCount: number;
    /**
     * The account was created by the submit rather than by the reader, so they
     * do not know they have one. Only true for the silent auto-create path —
     * someone who just signed up in the modal knows perfectly well.
     */
    accountAutoCreated?: boolean;
    onClose: () => void;
    /** New users: continue into the normal onboarding flow. */
    onExplore: () => void;
}

/** One enquiry per professional went out, so the count is the supplier count. */
const sentLine = (supplierCount: number) =>
    supplierCount === 1
        ? 'Your enquiry has been sent to the supplier.'
        : `Your enquiries have been sent to all ${supplierCount} suppliers.`;

/** Existing-account confirmation: short, then closes itself. */
const ExistingUserSuccess = ({
    supplierCount,
    onClose,
}: Pick<BulkEnquirySuccessProps, 'supplierCount' | 'onClose'>) => {
    const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

    useEffect(() => {
        if (secondsLeft <= 0) {
            onClose();
            return;
        }
        const timer = setTimeout(() => {
            setSecondsLeft((s) => s - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, onClose]);

    return (
        <div className="mx-auto flex min-h-0 w-full max-w-86 flex-1 flex-col">
            {/* Same shape as the single-enquiry screen — see it for why. */}
            <h2 className="mt-9 flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Enquiries sent
                <Check className="size-6 shrink-0" strokeWidth={2} />
            </h2>
            <p className="mt-3 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                {sentLine(supplierCount)}
            </p>
            <div className="flex-1" />
            <p className="mt-8 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
                You&rsquo;ll receive a notification here and via email as each
                supplier responds.
            </p>
            <GateStickyFooter className="mt-5" desktopClassName="mb-2">
                <button type="button" onClick={onClose} className={CTA_CLASS}>
                    Close
                </button>
                <p className="mt-4 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                    Closing in {secondsLeft}...
                </p>
            </GateStickyFooter>
        </div>
    );
};

/** Fresh-account confirmation: welcomes the member and leads into onboarding. */
const NewUserSuccess = ({
    supplierCount,
    accountAutoCreated,
    onClose,
    onExplore,
}: Omit<BulkEnquirySuccessProps, 'variant'>) => (
    <div className="mx-auto my-auto flex w-full max-w-86 flex-col">
        <h2 className="flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
            Enquiries sent
            <Check className="size-6 shrink-0" strokeWidth={2} />
        </h2>
        <p className="mt-5 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
            {sentLine(supplierCount)} We&rsquo;ll notify you as soon as replies
            come in.
        </p>
        {/* Where the Secure Sign-In offer belongs once BC-4831 lands: an
            auto-created account is passwordless, so a passkey is not an extra —
            it is how the reader gets back in at all. It takes the primary
            action here and Explore steps down to secondary. */}
        <p className="mt-4 text-[1.1875rem] leading-snug tracking-[-0.02em] text-[#828282]">
            {accountAutoCreated
                ? 'We’ve set up your ArchiPro account so you can track them — no password needed.'
                : 'In the meantime, you can continue planning your project with your saved products, design boards, and project tools.'}
        </p>
        <GateStickyFooter className="mt-8">
            <button type="button" onClick={onExplore} className={CTA_CLASS}>
                Explore my ArchiPro
            </button>
            <button
                type="button"
                onClick={onClose}
                className="mt-4 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
            >
                Close
            </button>
        </GateStickyFooter>
    </div>
);

/**
 * Success step of the bulk enquiry gate. Existing accounts get a short
 * confirmation that closes itself after {@link AUTO_CLOSE_SECONDS}; newly
 * registered accounts get a welcome screen whose primary CTA continues the
 * normal onboarding flow.
 */
export const BulkEnquirySuccess = (props: BulkEnquirySuccessProps) =>
    props.variant === 'existing' ? (
        <ExistingUserSuccess
            supplierCount={props.supplierCount}
            onClose={props.onClose}
        />
    ) : (
        <NewUserSuccess {...props} />
    );
